/*
 * The `admin.*` account routes of protocol.md §3.2 and the one instance setting an administrator
 * changes from outside the settings screen, typed once. Reading accounts is the people
 * area's (`peopleApi.list`, which answers at the caller's rung and therefore carries the addresses
 * for an admin); what is here is only what an administrator changes.
 *
 * Every write here is a step-up action in the matrix — tier 1, and tier 2 when the change makes
 * somebody an administrator (security.md §9.3) — so every call site asks for the tier: `withStepUp`
 * where the control stands alone, `useStepUpField` where it is already a form. The two reads
 * (`peopleApi.list` for the table, `readDefaultChannel` here) are admin cells without the U
 * condition and ask for nothing.
 *
 * A person's access is not written here. It is their membership of Administrators and Team members,
 * so a change of access is a group call (`core/groups/api.ts`), chosen by `access.ts`.
 *
 * The two invitation routes answer with the enrolment link itself. That is deliberate: the mail is
 * not always a usable channel, and an administrator who cannot hand over the first link cannot start
 * the team (internal/core/auth/http_admin.go).
 */

import { api } from "../net/api.ts"
import type { PersonView, Standing } from "../people/index.ts"

export interface CreateAccountInput {
	email: string
	handle: string
	displayName: string
	/**
	 * The standing the account starts with. The form sends `member`; the server also takes `guest`, and
	 * `admin` with the mailed code (the shell's `admin create --role`).
	 */
	role: Standing
}

/** What both invitation-issuing routes answer with. */
export interface InvitationIssued {
	invitationSentAt: number
	invitationLink: string
	invitationExpiresAt: number
}

export interface AccountCreated extends InvitationIssued {
	user: PersonView
}

export const adminApi = {
	createAccount(input: CreateAccountInput): Promise<AccountCreated> {
		return api.post<AccountCreated>("/api/admin/users", input)
	},

	reinvite(id: number): Promise<InvitationIssued> {
		return api.post<InvitationIssued>(`/api/admin/users/${id}/invitation`, {})
	},

	setState(id: number, state: "active" | "deactivated"): Promise<PersonView> {
		return api
			.put<{ user: PersonView }>(`/api/admin/users/${id}/state`, { state })
			.then((body) => body.user)
	},

	/** What the instance's default channel is; the three below are the round's one client-side half. */
	readDefaultChannel,
	/** Names the default channel, or clears it with 0. */
	writeDefaultChannel,
	/** Puts one account into the default channel. */
	addToDefaultChannel,
}

/*
 * The instance's default channel — the room everybody joins (`chat.default_channel`, U1). It is an
 * instance setting, so the value never reaches the client model (an instance setting is broadcast as
 * a version, never as a value: `protocol.md` §2.5.2) and the two surfaces that need it read it here:
 * the channel's own About block, which is where an administrator turns it on, and this area's
 * *New account* flow, which puts a brand-new account into it.
 *
 * Why the membership write stands in this file at all, beside routes that are core's own: the server
 * puts an account into the default channel at its next bootstrap (chat's own `Bootstrap`), so this
 * call is what makes a brand-new account a member before it first connects. The enrolment hook that
 * would do it instead would live in `internal/core/auth`, which is closed this round (PLAN §6). The
 * day that hook exists, `addToDefaultChannel` goes and nothing else moves. Core
 * imports no module tree for it — the route is named as the URL it is (architecture.md D21).
 */

/** The key of the setting, as the chat module registers it. */
export const DEFAULT_CHANNEL_KEY = "chat.default_channel"

/** One row of `GET /api/admin/settings`: the value is the setting's own JSON. */
interface InstanceSetting {
	key: string
	value: unknown
	version: number
}

/** What the instance's default channel is, and the version a write of it has to carry. */
export interface DefaultChannel {
	/** The channel everybody joins, or 0 while the instance has none. */
	conv: number
	/**
	 * The version the next write must name (`ifVersion`). It is 0 for a key nobody has written yet,
	 * which is why a first write cannot be a blind one either (`internal/core/settings/http_admin.go`).
	 */
	version: number
}

/**
 * The conversation id a stored value names. The setting holds it as a string — that is the shape the
 * module's validator accepts and the shape a cleared setting ("") has — and a number is read as well
 * so a hand-written value cannot turn the checkbox into a blank.
 */
function convOf(value: unknown): number {
	const id = typeof value === "number" ? value : Number(value)
	return Number.isSafeInteger(id) && id > 0 ? id : 0
}

/**
 * Reads the default channel. `GET /api/admin/settings` answers with every instance setting; a key no
 * version of the server registers is simply absent, and an absent key is "no default channel" rather
 * than a failure — the surfaces that ask only offer to set one.
 */
async function readDefaultChannel(): Promise<DefaultChannel> {
	const body = await api.get<{ settings: InstanceSetting[] }>("/api/admin/settings")
	const row = body.settings.find((setting) => setting.key === DEFAULT_CHANNEL_KEY)
	if (row === undefined) return { conv: 0, version: 0 }
	return { conv: convOf(row.value), version: row.version }
}

/**
 * Names the default channel, or clears it with 0. `ifVersion` is mandatory, and a version that is
 * not the current one is answered with `409 error.settings.versionConflict` — the caller shows that
 * sentence and re-reads rather than retrying, because the other value is somebody's decision.
 */
async function writeDefaultChannel(conv: number, ifVersion: number): Promise<DefaultChannel> {
	const body = await api.put<{ value: unknown; version: number }>(
		`/api/admin/settings/${DEFAULT_CHANNEL_KEY}`,
		{ value: conv === 0 ? "" : String(conv), ifVersion },
	)
	return { conv: convOf(body.value), version: body.version }
}

/**
 * Puts one account into the default channel with the whole history, which is what a channel
 * everybody is in is for. `chat.member.add` is an active member's cell (security.md §9.3), so an
 * administrator who is not in that channel is refused — and the refusal is shown rather than
 * swallowed: the account exists either way and the link still has to be handed over.
 */
function addToDefaultChannel(conv: number, userIds: number[]): Promise<void> {
	return api.post(`/api/chat/conversations/${conv}/members`, { userIds, history: "full" })
}
